import React from "react";
import { Link } from "react-router-dom";
import { Warning, Upload, ArrowRight } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  EPCLabel,
  getDisplayLabel,
  getEPCColor,
  type ProjectPartGroup,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";

interface EpcRefreshSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEpcLabel: EPCLabel | null;
  latestEpcDocumentDate: string | null;
  latestProjectPartDate: string | null;
  epcRefreshReason: "ENERGETIC" | "GENERIC" | null;
  projectPartGroups: ProjectPartGroup[];
  onUploadEpc: () => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Onbekende datum";
  try {
    return new Date(dateStr).toLocaleDateString("nl-NL");
  } catch {
    return dateStr;
  }
}

const EpcRefreshSuggestionModal: React.FC<EpcRefreshSuggestionModalProps> = ({
  isOpen,
  onClose,
  currentEpcLabel,
  latestEpcDocumentDate,
  latestProjectPartDate,
  epcRefreshReason,
  projectPartGroups,
  onUploadEpc,
}) => {
  const isEnergetic = epcRefreshReason === "ENERGETIC";

  const allParts = projectPartGroups.flatMap((g) =>
    g.parts.map((p) => ({ ...p, projectId: g.projectId, projectTitle: g.projectTitle })),
  );

  const triggeringParts = isEnergetic
    ? allParts.filter((p) => p.labelType === "energetisch")
    : allParts;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                isEnergetic ? "bg-brick-primary/10" : "bg-yellow-50"
              }`}
            >
              <Warning
                size={24}
                weight="fill"
                className={isEnergetic ? "text-brick-primary" : "text-yellow-500"}
              />
            </div>
            <DialogTitle className="text-neutral-primary">
              EPC mogelijk verouderd
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Reason summary */}
          <div
            className={`rounded-lg border p-4 ${
              isEnergetic
                ? "border-brick-primary/20 bg-brick-primary/5"
                : "border-yellow-200 bg-yellow-50"
            }`}
          >
            <p className="text-sm text-neutral-primary">
              {isEnergetic
                ? "Er zijn energetische renovatiewerken uitgevoerd na de meest recente EPC. Een nieuwe EPC is aanbevolen."
                : "Er zijn renovatiewerken uitgevoerd na de meest recente EPC. Een herziening van de EPC is aanbevolen."}
            </p>

            {/* EPC label transition: huidig → ? (na werken) */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-secondary-mid">
                Huidig EPC:
              </span>
              {currentEpcLabel ? (
                <span
                  className={`rounded px-2 py-0.5 text-xs font-semibold ${getEPCColor(currentEpcLabel)}`}
                >
                  {getDisplayLabel(currentEpcLabel)}
                </span>
              ) : (
                <span className="rounded bg-neutral-200 px-2 py-0.5 text-xs font-semibold text-neutral-secondary-mid">
                  Geen
                </span>
              )}
              <ArrowRight
                size={14}
                weight="bold"
                className="text-neutral-secondary-mid"
              />
              <span
                className="rounded border border-dashed border-neutral-primary/30 bg-white px-2 py-0.5 text-xs font-semibold text-neutral-secondary-mid"
                title="Nieuw label na de werken — nog onbekend tot een nieuwe EPC is opgeladen"
              >
                ?
              </span>
              <span className="text-xs text-neutral-secondary-mid">
                na de werken
              </span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-neutral-secondary-mid">
              <div>
                <span className="font-medium">Laatste EPC:</span>{" "}
                {formatDate(latestEpcDocumentDate)}
              </div>
              <div>
                <span className="font-medium">Laatste werken:</span>{" "}
                {formatDate(latestProjectPartDate)}
              </div>
            </div>
          </div>

          {/* Triggering project parts */}
          {triggeringParts.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-primary">
                {isEnergetic
                  ? "Energetische werken die een nieuwe EPC vereisen:"
                  : "Werken uitgevoerd na de EPC:"}
              </p>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {triggeringParts.map((part) => (
                  <div
                    key={part.id}
                    className="rounded-md border border-neutral-primary/10 bg-white p-3"
                  >
                    <p className="text-xs text-neutral-secondary-mid">
                      {part.projectTitle}
                    </p>
                    <Link
                      to={`/renovation/projects/${part.projectId}`}
                      className="text-sm font-medium text-moss-primary hover:underline"
                      onClick={onClose}
                    >
                      {part.title}
                    </Link>
                    {part.effectiveStartDate && (
                      <p className="mt-1 text-xs text-neutral-secondary-mid">
                        Startdatum: {formatDate(part.effectiveStartDate)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Sluiten
          </Button>
          <Button
            onClick={() => {
              onUploadEpc();
              onClose();
            }}
            className="gap-2 bg-moss-primary text-white hover:bg-moss-primary/90"
          >
            <Upload size={16} />
            Laad nieuwe EPC op
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EpcRefreshSuggestionModal;
